import type { FunctionViewModel, RiskAssessmentViewModel, TaskViewModel } from '@trinnvis/dabih-api-client';
import { BASE_PATH, EmployeeViewModelStatusEnum } from '@trinnvis/dabih-api-client';
import { riskAssessmentsNotDeleted, type StartTaskInstance } from 'src/store/selectors';
import {
  currentUserUuid,
  employeesAndContactsOptions,
  employeesNotDeleted,
  employeesShortNamesAndI,
  eventOccurrencesNotDeleted,
  functionsNotDeleted,
  getContactsAsUsers,
  getOrganization,
  key,
  singleUserVersion,
  startTasks,
  taskEventsInstances,
  toTaskViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { OrganizationState, State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import { sortedByName } from 'src/store/utilities.js';
import type { ApiClient } from 'src/store/backend';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { TaskEvent } from '../../library/lists/taskEvent';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { TaskView } from '../../content/tasks/task-view';
import type { TaskViewEditItem } from '../../content/tasks/task-view-edit-item';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';
import { shortenPath } from 'src/utilities/path';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { t } from 'src/i18n';

const smartTaskEventTextKeys: Record<string, string> = {
  employeeStart: 'tasks.smartTaskEventEmployeeStart',
  employeeLeave: 'tasks.smartTaskEventEmployeeLeave',
};

function _startTaskText(locked: boolean, taskUuid: string, startTasks: StartTaskInstance[]) {
  if (locked) {
    return t('tasks.startTaskFor', {
      time: startTasks.filter(function (item) {
        return item.uuid === taskUuid;
      })[0].timeDisplay,
    });
  }
  return '';
}

function functionsForAssigned(functions: FunctionViewModel[]): SelectDropdownOption[] {
  functions = sortedByName(functions);
  const common = functions.find((x) => x.type === 'COMMON');
  assertIsDefined(common);

  const otherFunctions = functions.filter((x) => x.type !== 'COMMON' && x.isConfirmedEntity);

  const list = [common, ...otherFunctions];

  return list.map((x) => ({ value: x.uuid, text: x.type === 'COMMON' ? t('tasks.all') : (x.name ?? '') }));
}

function functionsForResponsible(functions: FunctionViewModel[]): SelectDropdownOption[] {
  functions = sortedByName(functions);
  return functions
    .filter((item) => item.type === 'SINGLE' && item.isConfirmedEntity)
    .map((x) => ({ value: x.uuid, text: x.name ?? '' }));
}

function mapPagesPerFunction(functions: FunctionViewModel[]): Map<string, Set<number>> {
  const result = new Map<string, Set<number>>();
  functions
    .filter((x) => x.type !== 'COMMON')
    .forEach((f) => {
      result.set(f.uuid, new Set<number>(f.pages));
    });
  return result;
}

function getRelatedRiskAssessments(
  riskAssessments: RiskAssessmentViewModel[],
  uuid: string,
  baseHref: string,
): { name: string; href: string }[] {
  return riskAssessments
    .filter((riskAssessment) => {
      return riskAssessment.relatedTasks?.includes(uuid) && riskAssessment.name !== undefined;
    })
    .map((riskAssessment) => {
      return {
        name: riskAssessment.name ?? '',
        href: shortenPath(baseHref + '/riskAssessments/' + riskAssessment.uuid) ?? '',
      };
    });
}

function isEmergencyContent(riskAssessments: RiskAssessmentViewModel[], uuid: string) {
  return (
    riskAssessments.find((r) => {
      return r.pages?.includes(pageIds.emergency) && r.relatedTasks?.includes(uuid);
    }) !== undefined
  );
}

function getSituation(riskAssessments: RiskAssessmentViewModel[], entity: TaskViewModel & { functionName: string }) {
  const relatedRiskAssessments = riskAssessments.filter((r) => {
    return r.relatedTasks?.includes(entity.uuid);
  });
  if (relatedRiskAssessments.length) {
    const names = relatedRiskAssessments.filter((r) => r.name).map((r) => r.name);
    return names.join(', ') ?? '';
  }
  return entity.situation ?? '';
}

export async function buildTaskView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<TaskView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const availableFunctions = functionsForAssigned(functionsNotDeleted(state));
  const availableFunctionsForResponsible = functionsForResponsible(functionsNotDeleted(state));
  const item = organization.tasksById[uuid];
  assertEntityFound(item, 'tasks', uuid);

  const entity: TaskViewModel & { functionName: string } = toTaskViewModel(item, organization);
  const href = currentParent + '/tasks/' + uuid;
  const taskEvents = getTaskEvents(state, organization, uuid, href, entity.name || '');
  let smartTaskEventText = '';
  if (entity.smartTaskEvent) {
    const smartTaskEventTextKey = smartTaskEventTextKeys[entity.smartTaskEvent];
    if (smartTaskEventTextKey) {
      smartTaskEventText = t(smartTaskEventTextKey);
    }
  }
  const parentFunction = functionsNotDeleted(state).find((f) => f.uuid === entity.functionUuid);
  return {
    ...commonData,
    promoData: getPromoDataForEntity('tasks', item, getFeatureStates(state), parentFunction),
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state),
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: href,
    pdfHref:
      BASE_PATH +
      '/organizations/' +
      organization.organizationId +
      '/functions/' +
      entity.functionUuid +
      '/tasks/' +
      uuid +
      '.pdf?key=' +
      key(state),

    parentHref: currentParent,
    deleted: entity.deleted === true,
    type: 'tasks',
    name: entity.name ?? '',
    revisions: await fetchRevisions(
      api,
      'tasks',
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      entity.functionUuid,
      entity.contentLastModified,
    ),
    attachments: toAttachmentItems(api, 'tasks', entity.uuid, entity.attachments, entity.functionUuid),
    procedures: entity.procedures ?? '',
    parentEntityId: entity.functionUuid ?? '',
    assignedFunctionName: availableFunctions.find((f) => f.value === entity.functionUuid)?.text ?? '',
    responsibleFunctionName:
      availableFunctionsForResponsible.find((f) => f.value === entity.responsibleFunctionUuid)?.text ||
      t('content.noneResponsible'),
    situation: getSituation(riskAssessmentsNotDeleted(state), entity),
    taskEvents,
    startTaskText: _startTaskText(entity.locked === true, entity.uuid, startTasks(state)),
    availablePages: pagesAsSelectTagOptions(state),
    hasEvents: taskEvents.length > 0,
    pages: (entity.pages ?? []).map((x) => '' + x),
    responsibleFunctionUuid: entity.responsibleFunctionUuid ?? '',
    today: LocalDate.fromString(state.today),
    availableFunctions: availableFunctions,
    availableFunctionsForResponsible: availableFunctionsForResponsible,
    disabledPagesPerFunction: mapPagesPerFunction(functionsNotDeleted(state)),
    smartTaskEventText,
    deletable: true,
    availableEmployees: employeesNotDeleted(state)
      .filter((e) => e.status !== EmployeeViewModelStatusEnum.Terminated)
      .map((p) => ({ value: p.uuid, text: p.name })),
    availableContacts: getContactsAsUsers(state).map((p) => ({ value: p.uuid, text: p.name })),
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'tasks', uuid, organization.organizationId);
      return command.draft as TaskViewEditItem;
    },
    relatedRiskAssessments: getRelatedRiskAssessments(riskAssessmentsNotDeleted(state), uuid, href),
    emergencyContent: isEmergencyContent(riskAssessmentsNotDeleted(state), uuid),
  };
}

export function getTaskEvents(
  state: State,
  organization: OrganizationState,
  uuid: string,
  href: string,
  taskName: string,
): TaskEvent[] {
  const taskEventOccurrences = eventOccurrencesNotDeleted(state).filter((o) => {
    return o.taskUuid === uuid;
  });
  if (taskEventOccurrences.length === 0) {
    return [];
  } else {
    return taskEventsInstances(
      taskEventOccurrences,
      organization,
      employeesShortNamesAndI(state),
      functionsNotDeleted(state),
      getContactsAsUsers(state),
      organization.organizationId,
      currentUserUuid(state),
      singleUserVersion(state),
      LocalDate.now(),
      employeesAndContactsOptions(state),
    ).map((e) => {
      return {
        overdue: e.overdue ?? false,
        doneDate: e.doneDate ?? '',
        time: e.time,
        sortTime: e.dateTimeForOrdering ?? '',
        uuid: e.eventUuid,
        href: href + '/eventOccurrences/' + e.eventUuid,
        timeOfDayDisplay: e.timeOfDayDisplay,
        eventName: e.eventName !== taskName ? e.eventName : '',
        eventCurrentUser: e.myTask,
        secondaryLabel: e.overdue ? '' : (e.doneText ?? ''),
        rightLabel: e.assignee ?? '',
        rightLabelClass: 'assignee',
        hasDraft: e.hasDraft,
      };
    });
  }
}
